import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from '~/hooks/use-search-params';
import { BaseEntityGrid, ChannelGridTile, EntityGridBar } from '~/modules/entities/entity-grid';
import { organizationsListQueryOptions } from './query';

type OrgSearch = Parameters<typeof organizationsListQueryOptions>[0];

interface Props {
  fixedQuery?: Partial<OrgSearch>;
  focusView?: boolean;
  saveDataInSearch?: boolean;
  /** When true, show only 3 items with a "Show all" button */
  limitedView?: boolean;
}

const tileComponent = ChannelGridTile;

export function OrganizationsGrid({ fixedQuery, saveDataInSearch, focusView, limitedView: initialLimitedView }: Props) {
  const [expanded, setExpanded] = useState(false);
  const limitedView = initialLimitedView && !expanded;

  const { search: baseSearch, setSearch } = useSearchParams({ saveDataInSearch });

  const search: OrgSearch = { ...baseSearch, ...(fixedQuery ?? {}) };

  const queryOptions = organizationsListQueryOptions(search);

  const q = search.q ?? '';
  const isFiltered = !!q;

  const { data, isFetching, isLoading, error, hasNextPage, fetchNextPage } = useInfiniteQuery({
    ...queryOptions,
    select: (data) => data.pages.flatMap((p) => p.items),
  });

  const entities = data;

  return (
    <div className="flex h-full flex-col gap-2">
      {!limitedView && (
        <EntityGridBar
          queryKey={queryOptions.queryKey}
          searchVars={baseSearch}
          label={'c:organization'}
          entityType="organization"
          // Discovery grid: a role filter collapses the scope to membership-only; bar stays for short lists
          roleFilter={false}
          alwaysShow
          setSearch={setSearch}
          focusView={focusView}
        />
      )}

      <BaseEntityGrid
        label="c:organization"
        tileComponent={tileComponent}
        entities={entities}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFiltered={isFiltered}
        limitedView={limitedView}
        onExpand={() => setExpanded(true)}
        queryKey={queryOptions.queryKey}
      />
    </div>
  );
}
